/**
 * price相关函数
 */
import { type MiniImprintMethodModel, ProductSpecModel } from '../model'
import {
  type PriceInfo,
  type ProductSpec,
  type SpecGroup,
  type MiniSpecGroup,
  OrderType
} from '../types'
import { isEmptyObject } from '../utils/validate'
import { useOrder } from './use-order'
import { getTotalQty } from './use-quantity'
import { useWindow } from './use-window'

export type getPriceListOptions = {
  isBlank: boolean
  productSpecList: ProductSpecModel[]
  specList?: MiniSpecGroup[]
  addonList?: MiniSpecGroup[]
  checkQty?: boolean
  imprintMethodList: MiniImprintMethodModel[]
}

const { priceList } = useWindow()

export function getBlankPriceList (): PriceInfo[] {
  return priceList.filter((it) => {
    return it.BlankLimitation === 0 || it.BlankLimitation === 2
  })
}

export function getImprintPriceList (): PriceInfo[] {
  return priceList.filter((it) => {
    return it.BlankLimitation === 0 || it.BlankLimitation === 1
  })
}

/**
 * 根据price list和spec list查找spec limitation
 * @param {any} priceList
 * @returns 结果 {color:['red','blur']}
 */
export function getSpecListByPriceList (
  priceList: PriceInfo[],
  productSpecList: ProductSpec[],
  specList: SpecGroup[],
  addonList: SpecGroup[]
): Record<string, string[]> {
  // const { ProductSpecList, SpecList, AddonList } = (window as any).productData

  const spList: any[] = []
    ;[productSpecList, specList, addonList].forEach((it: any) => {
      spList.push(
        ...it.map((sp: any) => {
          return {
            name: sp.name,
            values: sp.values.filter((f: any) => f.value !== '-1').map((val: any) => val.value)
          }
        })
      )
    })

  // sepc存的值，默认是[],如果值是false表示支持所有的，如果是array表示有spec limitation限制
  const specLimitation: Record<string, string[]> = {}
  spList.forEach((spec: any) => {
    const name = spec.name
    specLimitation[name] = [] // 默认初始化为数组类型
    for (let i = 0; i < priceList.length; i++) {
      const price = priceList[i]
      // 如果是空SpecLimitation，表示支持所有的
      if (isEmptyObject(price.SpecLimitation)) {
        specLimitation[name] = spec.values.map((val: any) => {
          return val
        })
        break
      } else {
        if (!price.SpecLimitation[spec.name]) {
          specLimitation[spec.name] = spec.values.map((val: any) => {
            return val
          })
          break
        } else {
          // 一次添加多个数据放到数组里
          // specLimitation[spec.name].push(...price.SpecLimitation[spec.name])
          Array.prototype.push.apply(
            specLimitation[spec.name],
            price.SpecLimitation[spec.name]
          )
        }
      }
    }
  })
  return specLimitation
}

/**
 *
 * @returns price 相关的函数
 */

export const getPriceList = (options: getPriceListOptions): PriceInfo[] => {
  // 第一次过滤，区分blank和imprint
  const allPriceList = options.isBlank ? getBlankPriceList() : getImprintPriceList()

  const { isBlank, productSpecList, specList, addonList, imprintMethodList } =
    options

  const { orderType } = useOrder();

  // 购买的商品总数
  const totalQty = getTotalQty(productSpecList)

  // 开始第二次过滤，根据所选的product spec过滤
  const _getPriceList = (checkQty: boolean) => {
    if (productSpecList && productSpecList.length > 0) {
      return allPriceList.filter((it) => {
        const specLimitation = it.SpecLimitation
        if (checkQty) {
          if (it.MinQty > 0 && it.MinQty > totalQty) return false;
          if (it.MaxQty > 0 && it.MaxQty < totalQty) return false;
        }
        if (isEmptyObject(specLimitation)) return true

        for (let i = 0; i < productSpecList.length; i++) {
          const productSpec = productSpecList[i].productSpec
          for (let j = 0; j < productSpec.length; j++) {
            const el = productSpec[j]
            if (!el.value) continue

            if (
              specLimitation[el.name] &&
              (specLimitation[el.name] as any[]).includes(el.value.value) ===
              false
            ) {
              return false
            }
          }
        }
        return true
      })
    }
    return allPriceList;
  }
  let priceList = _getPriceList(orderType !== OrderType.SpecSample);
  if (orderType !== OrderType.SpecSample && priceList.length === 0) {
    priceList = _getPriceList(false);
  }

  // 如果有spec，那就要检查是否有限制
  if (specList && specList.length > 0) {
    priceList = priceList.filter((it) => {
      const specLimitation = it.SpecLimitation
      if (isEmptyObject(specLimitation)) return true

      for (let i = 0; i < specList.length; i++) {
        const el = specList[i]
        if (!el.selectedItem) continue

        if (
          specLimitation[el.name] &&
          (specLimitation[el.name] as any[]).includes(el.selectedItem.value) === false
        ) {
          return false
        }
      }
      return true
    })
  }

  // 如果有addon，那就要检查是否有限制
  if (addonList && addonList.length > 0) {
    priceList = priceList.filter((it) => {
      const specLimitation = it.SpecLimitation
      if (isEmptyObject(specLimitation)) return true

      for (let i = 0; i < addonList.length; i++) {
        const el = addonList[i]
        if (!el.selectedItem) continue

        if (
          specLimitation[el.name] &&
          (specLimitation[el.name] as any[]).includes(el.selectedItem.value) === false
        ) {
          return false
        }
      }
      return true
    })
  }

  if (isBlank === false && imprintMethodList) {
    if (imprintMethodList.length > 0) {
      priceList = priceList.filter((it) => {
        // 先计算颜色数量
        // 获得所选中的颜色数
        let totalColors = 0

        // 获取总的选中的颜色数量
        for (let i = 0; i < imprintMethodList.length; i++) {
          totalColors +=
            imprintMethodList[i].decorationMethod.selectedColors.length
        }

        // 先判断是否满足颜色限制
        if (totalColors > it.MaxColors) {
          return false
        }

        if (it.ImprintMethodLimitation.length === 0) return true

        return options.imprintMethodList.every((m) =>
          it.ImprintMethodLimitation.includes(m.decorationMethod.Method)
        )
      })
    }
  }
  return priceList
}

/**
 * 更新多Size时商品差价
 * @param {any} currentItem
 */
export const updatePriceQtyForMultiSize = (
  priceList: PriceInfo[],
  selectedProductSpecList: ProductSpecModel[]
) => {
  if (selectedProductSpecList.length === 0) return

  // 获取当前用户购买的数量
  const totalQty = getTotalQty(selectedProductSpecList)

  //
  priceList = priceList.filter(function (item) {
    return totalQty >= item.MinQty
  })

  // 先把满足条件的price list按照price name分组，然后依次遍历计算出最小price和price之间的差价
  // 该算法有个问题，如果有多个，那只会计算第一个price name的。
  const priceMap = new Map<string, PriceInfo>()

  // priceList.forEach((it) => {
  //   priceMap.set(it.PriceName, it);
  // });

  const priceNames = Array.from(new Set(priceList.map((f) => f.PriceName)))

  priceNames.some((priceName, index) => {
    const sizeList = priceList
      .filter(function (item) {
        return item.PriceName === priceName
      })
      .reduce(function (prev, next) {
        // 返回{M:Price Object,L:Price Object}这样的格式
        next.Size.forEach(function (size) {
          prev[size] = next
        })
        return prev
      }, {} as Record<string, PriceInfo>)

    /*
     * 下面代码计算出最小 price，其他价格都计算差价
     * 返回[{size:'M',price:10.00},{size:'L',price:20.00}]
     */
    let minPrice = 0
    const list = Object.keys(sizeList).map((key) => {
      const item = sizeList[key]
      let unitPrice = 0
      Object.keys(item.Price)
      for (const key in item.Price) {
        const qty = parseInt(key)
        if (totalQty < qty) {
          break
        } else {
          unitPrice = item.Price[qty]
        }
      }

      minPrice = minPrice === 0 ? unitPrice : Math.min(minPrice, unitPrice)
      return { size: key, price: unitPrice }
    })

    // 循环计算差价
    const model = selectedProductSpecList[0]
    list.forEach(function (item) {
      if (Array.isArray(model.quantity)) {
        for (let i = 0; i < model.quantity.length; i++) {
          if (model.quantity[i].size === item.size) {
            model.quantity[i].price = item.price - minPrice
          }
        }
      }
    })
  })
}
